import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import ReactModal from 'react-modal';
import * as XLSX from 'xlsx';

// Ant Design imports
import {
    Table as AntTable,
    Button as AntButton,
    Modal as AntModal,
    Form,
    Input,
    Select as AntSelect,
    Space,
    message,
    Popconfirm,
    Tag,
    Row,
    Col,
    Switch,
    InputNumber,
    Alert,
    // Pagination // AntTable handles its own pagination now
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CloseOutlined,
    SaveOutlined,
    DownloadOutlined,
    SyncOutlined,
    SearchOutlined,
    FileExcelOutlined,
    UpOutlined,
    DownOutlined
} from '@ant-design/icons';
import Layout from './Layout';

// Option for Ant Design Select component
const { Option } = AntSelect;

ReactModal.setAppElement('#root');

const TASK_STATUS_OPTIONS = [
    { value: 'new', label: 'Новая' },
    { value: 'in_progress', label: 'В работе' },
    { value: 'filled', label: 'Заполнена' },
    { value: 'rework', label: 'На доработке' },
    { value: 'approval', label: 'Согласование' },
    { value: 'analysis', label: 'Требует анализа' },
    { value: 'approved', label: 'Утверждено' }
];

const checkDateTimeCondition = async (dateToCheck, condition) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.post('/api/tasks/check-date-condition',
            {
                dateToCheck: dateToCheck,
                condition: condition,
            },
            {
                headers: { 'Authorization': `Bearer ${token}` }
            }
        );
        return response.data.isMatch;
    } catch (error) {
        console.error("Ошибка при проверке условия даты через API:", error);
        return false;
    }
};

// ActiveMap отдаёт дробные значения с запятой («5,1»), parseFloat отрезал бы десятые
const toNumber = (value) => {
    if (typeof value === 'number') return value;
    if (value === null || value === undefined) return NaN;
    return parseFloat(String(value).replace(/\s/g, '').replace(',', '.'));
};

const calculateTemplateVariables = (taskData, customFields, counterparty, getDynamicValue) => {
    const roadToMinutes = toNumber(getDynamicValue('<Doroga_do_obiekta_(minut)>', taskData, customFields)) || 0;
    const roadFromMinutes = toNumber(getDynamicValue('<Doroga_s_obiekta_(minut)>', taskData, customFields)) || 0;
    const workMinut = toNumber(getDynamicValue('<Vremja_na_rabote>', taskData, customFields)) || 0;
    const workTimeHours = Math.round((workMinut / 60) * 10) / 10;
    const mileageKm = toNumber(getDynamicValue('<Probeg,_km>', taskData, customFields)) || 0;
    const dieselMinutes = toNumber(getDynamicValue('<CHase_dizelenija>', taskData, customFields)) || 0;

    const fuelConsumption = parseFloat(counterparty?.fuel_consumption) || 0;

    const travelTimeHours = Math.ceil(((roadToMinutes + roadFromMinutes) / 60) * 10) / 10;
    const totalTimeHours = workTimeHours + travelTimeHours;
    const fuelSpentLiters = (mileageKm * fuelConsumption) / 100;
    const dieselHoursRounded = Math.ceil((dieselMinutes / 60) * 10) / 10;
    const dieselHoursMinusOne = Math.max(dieselHoursRounded - 1, 0);

    return {
        'время_в_дороге': travelTimeHours,
        'общее_время': totalTimeHours,
        'затрачено_топлива_на_авто': fuelSpentLiters,
        'время_дизеления': dieselHoursRounded,
        'время_дизеления_минус1': dieselHoursMinusOne,
    };
};

const resolveTemplateValue = (placeholder, taskData, customFields, counterparty, calculatedVars, getDynamicValueFunc) => {
    if (typeof placeholder !== 'string') {
        return placeholder;
    }
    const trimmedPlaceholder = placeholder.trim();

    if (trimmedPlaceholder.startsWith('$') && trimmedPlaceholder.endsWith('$')) {
        const varName = trimmedPlaceholder.slice(1, -1);
        return calculatedVars[varName] !== undefined ? calculatedVars[varName] : null;
    }

    if (trimmedPlaceholder.startsWith('**') && trimmedPlaceholder.endsWith('**')) {
        const cpFieldName = trimmedPlaceholder.slice(2, -2);
        return counterparty?.[cpFieldName] !== undefined ? counterparty[cpFieldName] : null;
    }

    if (trimmedPlaceholder.startsWith('<') && trimmedPlaceholder.endsWith('>')) {
        return getDynamicValueFunc(trimmedPlaceholder, taskData, customFields);
    }

    return placeholder;
};

const TasksPage = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalTasks, setTotalTasks] = useState(0);

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentTaskId, setCurrentTaskId] = useState(null);
    const [taskId, setTaskId] = useState(null); // Backend ID
    const [currentTaskStatus, setCurrentTaskStatus] = useState('new');
    const [invoices, setInvoices] = useState({
        income: [],
        expense: []
    });
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        income: true,
        expense: true,
        basic: false,
        history: false,
    });
    const [counterparties, setCounterparties] = useState([]);
    const [nomenclatures, setNomenclatures] = useState([]);
    const [financialArticles, setFinancialArticles] = useState([]);
    const [priceTypesList, setPriceTypesList] = useState([]);
    const [externalTaskData, setExternalTaskData] = useState(null);

    // Inline adding/editing states for invoices
    const [newInvoice, setNewInvoice] = useState({
        type: 'income',
        counterparty: null,
        article: null,
        rate_name: '',
        selectedRateOption: null,
        unit: '', quantity: 1, percent: 100, price: 0, note: '', penalty: 0, include: true
    });
    const [editingInvoiceId, setEditingInvoiceId] = useState(null);
    const [isAddingNewInvoice, setIsAddingNewInvoice] = useState(false);

    const [scenarioCheckLoading, setScenarioCheckLoading] = useState(false);
    const [matchingScenarios, setMatchingScenarios] = useState([]);
    const [scenarioModalIsOpen, setScenarioModalIsOpen] = useState(false);

    const [invoiceForm] = Form.useForm();

    // --- Состояния для опций фильтров ---
    const [filterOptions, setFilterOptions] = useState({
        taskStatuses: [], // For 'onlyStatus'
        taskTypes: [],    // For 'type_id'
        organizations: [], // For 'assignedOrganizationId' and 'organizationId'
        customers: [],     // For 'customer_id' (if implemented)
    });

    const [apiFilters, setApiFilters] = useState({
        onlyStatus: [],
        stage: [],
        type_id: [],
        assignedOrganizationId: [],
        organizationId: [],
        assigned_user_fio: '',
        geDateLocal: '',
        leDateLocal: '',
        status_invoices: '',
    });

    // --- useEffect для загрузки опций фильтров ---
    useEffect(() => {
        const fetchFilterOptions = async () => {
            const token = localStorage.getItem('token');
            try {
                const [
                    taskStatusesRes,
                    taskTypesRes,
                    organizationsRes,
                    // customersRes // Uncomment if you have /api/customers endpoint
                ] = await Promise.all([
                    axios.get('/api/task-statuses', { headers: { 'Authorization': `Bearer ${token}` } }),
                    axios.get('/api/task-types', { headers: { 'Authorization': `Bearer ${token}` } }),
                    axios.get('/api/organizations', { headers: { 'Authorization': `Bearer ${token}` } }),
                    // axios.get('/api/customers', { headers: { 'Authorization': `Bearer ${token}` } }),
                ]);

                setFilterOptions(prev => ({
                    ...prev,
                    // Ensure options are in { value, label } format
                    taskStatuses: (taskStatusesRes.data || []).map(item => ({ value: item.id, label: item.name })),
                    taskTypes: (taskTypesRes.data || []).map(item => ({ value: item.id, label: item.name })),
                    organizations: (organizationsRes.data || []).map(item => ({ value: item.id, label: item.name })),
                    // customers: (customersRes.data || []).map(item => ({ value: item.id, label: item.name })),
                }));
            } catch (error) {
                console.error('Ошибка при загрузке опций фильтров:', error);
                message.error('Не удалось загрузить некоторые опции для фильтров.');
            }
        };

        fetchFilterOptions();
    }, []); // Загружаем опции фильтров только при монтировании компонента


    const handleApiFilterChange = (filterName, value) => {
        setApiFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const handleMultiSelectChange = (filterName, selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
        handleApiFilterChange(filterName, values);
    };

    const getDynamicValue = (placeholder, taskData, customFields) => {
        if (typeof placeholder !== 'string' || !placeholder.startsWith('<') || !placeholder.endsWith('>')) {
            return placeholder;
        }

        const fieldName = placeholder.slice(1, -1);

        if (Object.prototype.hasOwnProperty.call(taskData, fieldName)) {
            return taskData[fieldName];
        }

        const customFieldKey = Object.keys(customFields).find(
            k => k.toLowerCase() === fieldName.toLowerCase()
        );

        if (customFieldKey && customFields[customFieldKey]) {
            return customFields[customFieldKey].value;
        }

        return null;
    };

    const applySelectedScenario = async (scenario) => {
        setScenarioModalIsOpen(false);
        setScenarioCheckLoading(true);

        console.clear();
        console.log(`%c--- ЗАПУСК ПРИМЕНЕНИЯ СЦЕНАРИЯ: "${scenario.scenario_name}" ---`, 'color: #8A2BE2; font-size: 1.2em; font-weight: bold;');

        try {
            const token = localStorage.getItem('token');
            // !!! ИЗМЕНЕНИЕ: Запрашиваем все шаблоны и фильтруем на клиенте
            // Это НЕ масштабируемое решение для большого количества шаблонов.
            // Если шаблонов много, потребуется изменить бэкенд для фильтрации по JSON-полю.
            const allTemplatesResponse = await axios.get('/api/templates/all', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const allTemplates = allTemplatesResponse.data || [];

            // Фильтруем шаблоны на клиенте по scenario_ids
            const templates = allTemplates.filter(template =>
                template.scenario_ids && Array.isArray(template.scenario_ids) && template.scenario_ids.includes(scenario.id)
            );

            console.log(`Получено ${templates ? templates.length : 0} шаблонов для этого сценария:`, templates);

            if (!templates || templates.length === 0) {
                AntModal.warning({
                    title: 'Нет шаблонов',
                    content: `Для сценария "${scenario.scenario_name}" не найдено настроенных шаблонов.`,
                });
                setScenarioCheckLoading(false);
                return;
            }

            const customFields = externalTaskData.custom_fields ? JSON.parse(externalTaskData.custom_fields) : {};
            const newInvoicesFromScenario = { income: [], expense: [] };
            let warnings = [];

            console.log("--- НАЧАЛО ОБРАБОТКИ КАЖДОГО ШАБЛОНА ---");

            for (const template of templates) {
                console.log(`\n%c* Обрабатываем шаблон ID: ${template.id}`, 'color: blue; font-weight: bold;');
                console.log("  - Данные шаблона:", template);

                let counterparty = null;
                const counterpartyNamePlaceholder = template.counterparty;
                console.log(`  - Ищем контрагента. Плейсхолдер: "${counterpartyNamePlaceholder}"`);

                if (counterpartyNamePlaceholder && counterpartyNamePlaceholder.includes('+')) {
                    const parts = counterpartyNamePlaceholder.split('+').map(p => p.trim());
                    const primaryPlaceholder = parts[0];
                    const secondaryFieldName = parts[1];

                    console.log(`    - Сложный плейсхолдер. Часть 1 (основной КА): "${primaryPlaceholder}", Часть 2 (поле связи): "${secondaryFieldName}"`);

                    const primaryCounterpartyName = getDynamicValue(primaryPlaceholder, externalTaskData, customFields);
                    console.log(`    - Значение для основного КА: "${primaryCounterpartyName}"`);

                    if (primaryCounterpartyName) {
                        const primaryCounterparty = counterparties.find(c => c.name === primaryCounterpartyName);
                        if (primaryCounterparty) {
                            console.log(`    - Найден основной КА:`, primaryCounterparty);
                            const secondaryCounterpartyId = primaryCounterparty[secondaryFieldName];
                            console.log(`    - ID связанной сущности из поля "${secondaryFieldName}": ${secondaryCounterpartyId}`);
                            if (secondaryCounterpartyId) {
                                counterparty = counterparties.find(c => c.id === secondaryCounterpartyId);
                                if (counterparty) {
                                    console.log(`    - УСПЕХ: Найден связанный КА по ID ${secondaryCounterpartyId}:`, counterparty);
                                } else {
                                    warnings.push(`Не найден связанный контрагент (машина) с ID: ${secondaryCounterpartyId} для основного контрагента "${primaryCounterpartyName}".`);
                                    console.error(`  - ОШИБКА: Не найден связанный КА (машина) с ID ${secondaryCounterpartyId}.`);
                                }
                            } else {
                                warnings.push(`У контрагента "${primaryCounterpartyName}" не заполнено поле "${secondaryFieldName}".`);
                                console.error(`  - ОШИБКА: У основного КА "${primaryCounterpartyName}" пустое поле "${secondaryFieldName}".`);
                            }
                        } else {
                            warnings.push(`Не найден основной контрагент "${primaryCounterpartyName}".`);
                            console.error(`  - ОШИБКА: Не найден основной КА с именем "${primaryCounterpartyName}".`);
                        }
                    }
                } else {
                    const counterpartyName = getDynamicValue(counterpartyNamePlaceholder, externalTaskData, customFields);
                    if (counterpartyName) {
                        counterparty = counterparties.find(c => c.name === counterpartyName);
                        console.log(`    - Простой плейсхолдер. Результат: "${counterpartyName}". Найден? ${!!counterparty}`);
                    }
                }

                if (!counterparty) {
                    if (counterpartyNamePlaceholder) {
                        warnings.push(`Контрагент по условию "${counterpartyNamePlaceholder}" не найден.`);
                        console.error(`  - ОШИБКА: Итоговый контрагент не найден.`);
                    }
                    continue;
                }
                console.log("  - Итоговый контрагент для этого шаблона:", counterparty);

                const calculatedVars = calculateTemplateVariables(externalTaskData, customFields, counterparty, getDynamicValue);
                console.log("  - Рассчитанные переменные:", calculatedVars);


                const article = financialArticles.find(a => a.name === template.article_id);
                if (!article) {
                    warnings.push(`Статья "${template.article_id}" из шаблона не найдена в справочнике.`);
                    console.error(`  - ОШИБКА: Статья "${template.article_id}" не найдена.`);
                    continue;
                }
                console.log("  - Статья найдена:", article);

                const nomenclature = nomenclatures.find(n => n.name === template.nomenclature_id);
                if (!nomenclature) {
                    warnings.push(`Номенклатура "${template.nomenclature_id}" из шаблона не найдена в справочнике.`);
                    console.error(`  - ОШИБКА: Номенклатура "${template.nomenclature_id}" не найдена.`);
                    continue;
                }
                console.log("  - Номенклатура найдена:", nomenclature);

                console.log(`  - Ищем цену для номенклатуры "${nomenclature.name}". Прайс-листы контрагента:`, counterparty.price_lists);
                let selectedRate = null;
                for (const priceTypeIdStr in nomenclature.prices) {
                    const priceTypeId = parseInt(priceTypeIdStr, 10);
                    console.log(`    - Проверяем тип цены ID: ${priceTypeId}`);
                    if (counterparty.price_lists.includes(priceTypeId)) {
                        const priceTypeInfo = priceTypesList.find(pt => pt.id === priceTypeId);
                        const priceValue = nomenclature.prices[priceTypeId];
                        if (typeof priceValue !== 'number') {
                            console.warn(`  - ПРЕДУПРЕЖДЕНИЕ: Цена для типа ID ${priceTypeId} не является числом (значение: ${priceValue}). Пропускаем.`);
                            continue;
                        }

                        selectedRate = {
                            value: `${nomenclature.id}-${priceTypeId}`,
                            label: `${nomenclature.name} (${priceTypeInfo ? priceTypeInfo.name : `Тип ${priceTypeId}`}: ${priceValue.toLocaleString('ru-RU')})`,
                            name: nomenclature.name,
                            unit: nomenclature.unit,
                            priceValue: priceValue,
                        };

                        console.log(`    - НАЙДЕНА подходящая цена!`, selectedRate);
                        break;
                    }
                }

                if (!selectedRate) {
                    warnings.push(`Для номенклатуры "${nomenclature.name}" не найдено подходящей цены для контрагента "${counterparty.name}".`);
                    console.error(`  - ОШИБКА: Подходящая цена не найдена.`);
                    continue;
                }

                const quantityPlaceholder = template.quantity;
                const notePlaceholder = template.note;
                const penaltyPlaceholder = template.penalty;
                const percentPlaceholder = template.percentage;

                console.log("  - Рассчитываем значения полей из плейсхолдеров:");
                console.log(`    - Количество (плейсхолдер: "${quantityPlaceholder}")`);
                console.log(`    - Примечание (плейсхолдер: "${notePlaceholder}")`);
                console.log(`    - Штраф (плейсхолдер: "${penaltyPlaceholder}")`);
                console.log(`    - Процент (плейсхолдер: "${percentPlaceholder}")`);

                const quantity = toNumber(resolveTemplateValue(quantityPlaceholder, externalTaskData, customFields, counterparty, calculatedVars, getDynamicValue)) || 1;
                const note = resolveTemplateValue(notePlaceholder, externalTaskData, customFields, counterparty, calculatedVars, getDynamicValue) || '';
                const penalty = toNumber(resolveTemplateValue(penaltyPlaceholder, externalTaskData, customFields, counterparty, calculatedVars, getDynamicValue)) || 0;
                const percent = toNumber(resolveTemplateValue(percentPlaceholder, externalTaskData, customFields, counterparty, calculatedVars, getDynamicValue)) || 100;
                const price = selectedRate.priceValue;

                console.log("  - Итоговые значения:");
                console.log(`    - Количество: ${quantity}`);
                console.log(`    - Цена: ${price}`);
                console.log(`    - Процент: ${percent}`);
                console.log(`    - Примечание: "${note}"`);
                console.log(`    - Штраф: ${penalty}`);

                const newInvoiceRow = {
                    id: `new-${Date.now()}-${Math.random()}`,
                    type: template.type,
                    counterparty: counterparty.name,
                    article: article.name,
                    rate_name: nomenclature.name,
                    unit: nomenclature.unit,
                    price: price,
                    quantity: quantity,
                    percent: percent,
                    note: note,
                    penalty: penalty,
                    include: template.is_active !== false,
                    amount: price * quantity * (percent / 100),
                };

                console.log("  - Сформирована новая строка счета:", newInvoiceRow);
                newInvoicesFromScenario[template.type].push(newInvoiceRow);
            }

            console.log("\n%c--- ЗАВЕРШЕНИЕ ПРИМЕНЕНИЯ СЦЕНАРИЯ ---", 'color: #8A2BE2; font-size: 1.2em; font-weight: bold;');
            console.log("Новые строки для добавления:", newInvoicesFromScenario);
            console.log("Предупреждения:", warnings);

            setInvoices(prev => ({
                income: [...prev.income, ...newInvoicesFromScenario.income],
                expense: [...prev.expense, ...newInvoicesFromScenario.expense]
            }));

            if (warnings.length > 0) {
                AntModal.warning({
                    title: 'Шаблон применен с предупреждениями',
                    content: (
                        <div>
                            <p>Следующие предупреждения были обнаружены:</p>
                            <ul>
                                {warnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                        </div>
                    ),
                });
            } else {
                message.success(`Шаблон "${scenario.scenario_name}" успешно применен!`);
            }

        } catch (error) {
            console.error("Критическая ошибка при применении шаблона сценария:", error);
            AntModal.error({
                title: 'Ошибка при применении шаблона',
                content: 'Не удалось применить шаблон. Проверьте консоль для деталей.',
            });
        } finally {
            setScenarioCheckLoading(false);
        }
    };


    useEffect(() => {
        if (!searchText.trim()) {
            fetchTasks();
        }
    }, [currentPage, pageSize, searchText])

    const fetchTasks = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            let response;
            if (searchText && searchText.trim() !== '') {
                try {
                    response = await axios.get(`/api/tasks/${encodeURIComponent(searchText.trim())}`, {
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                    });

                    if (response.data && !Array.isArray(response.data)) {
                        setTasks([response.data]);
                        setTotalTasks(1);
                    } else if (Array.isArray(response.data)) {
                        setTasks(response.data);
                        setTotalTasks(response.data.length);
                    } else {
                        setTasks([]);
                        setTotalTasks(0);
                    }
                } catch (error) {
                    if (error.response && error.response.status === 404) {
                        setTasks([]);
                        setTotalTasks(0);
                        message.warning(`Задача с ID "${searchText}" не найдена.`);
                    } else {
                        message.error('Ошибка при поиске задачи по ID');
                        console.error(error);
                        setTasks([]);
                        setTotalTasks(0);
                    }
                }
            } else {
                const params = {
                    page: currentPage,
                    limit: pageSize,
                };

                for (const key in apiFilters) {
                    const value = apiFilters[key];
                    if (Array.isArray(value) && value.length > 0) {
                        params[key] = value.join(',');
                    } else if (typeof value === 'string' && value.trim() !== '') {
                        if (key === 'geDateLocal' || key === 'leDateLocal') {
                            params[key] = value.replace('T', ' ');
                        } else if (key !== 'status_invoices' && key !== 'assigned_user_fio') {
                            params[key] = value;
                        }
                    }
                }

                response = await axios.get('/api/tasks', {
                    params,
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const tasksData = response.data || [];
                console.log(tasksData);
                setTasks(tasksData);
                setTotalTasks(response.headers['x-total-count'] ? parseInt(response.headers['x-total-count'], 10) : tasksData.length);
            }
        } catch (error) {
            if (!(searchText && searchText.trim() !== '')) {
                message.error('Ошибка при загрузке задач');
                console.error(error);
                setTasks([]);
                setTotalTasks(0);
            }
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const activeFilters = {};
            for (const key in apiFilters) {
                const value = apiFilters[key];
                if (Array.isArray(value) && value.length > 0) {
                    activeFilters[key] = value.join(',');
                } else if (typeof value === 'string' && value.trim() !== '') {
                    if (key === 'geDateLocal' || key === 'leDateLocal') {
                        activeFilters[key] = value.replace('T', ' ');
                    } else if (key !== 'status_invoices' && key !== 'assigned_user_fio') {
                        activeFilters[key] = value;
                    }
                }
            }

            const response = await axios.post('/api/tasks/filter-and-sync', activeFilters, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            message.success(response.data.message || `Синхронизировано ${response.data.synced} задач.`);

            setCurrentPage(1);
            await fetchTasks();

        } catch (error) {
            console.error("Ошибка при применении фильтров и синхронизации:", error);
            message.error(error.response?.data?.error || 'Не удалось применить фильтры.');
        } finally {
            setLoading(false);
        }
    };

    const resetFilters = () => {
        setApiFilters({
            onlyStatus: [], stage: [], type_id: [],
            assignedOrganizationId: [], organizationId: [],
            assigned_user_fio: '', geDateLocal: '', leDateLocal: '',
            status_invoices: '',
        });
        setCurrentPage(1); // Сброс страницы при сбросе фильтров
        setSearchText(''); // Сброс текстового поиска
        fetchTasks();
    };

    const syncTasks = async () => {
        const token = localStorage.getItem('token');
        try {
            message.loading({ content: 'Синхронизация задач...', key: 'syncing' });
            await axios.get('/api/tasks/sync', { headers: { 'Authorization': `Bearer ${token}` } });
            fetchTasks();
            message.success({ content: 'Синхронизация завершена', key: 'syncing', duration: 2 });
        } catch (error) {
            message.error({ content: 'Ошибка при синхронизации задач', key: 'syncing', duration: 3 });
            console.error(error);
        }
    };

    const fetchPrimaryDictionaries = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/counterparties?limit=1000', { headers: { 'Authorization': `Bearer ${token}` } });
            const responseData = response.data;
            setCounterparties(Array.isArray(responseData?.data) ? responseData.data : []);
            setFinancialArticles(Array.isArray(responseData?.options?.articles) ? responseData.options.articles : []);
            setPriceTypesList(Array.isArray(responseData?.options?.priceTypes) ? responseData.options.priceTypes : []);
        } catch (error) {
            console.error('Ошибка при загрузке основных справочников:', error);
            setCounterparties([]);
            setFinancialArticles([]);
            setPriceTypesList([]);
            throw error;
        }
    };

    const fetchNomenclaturesOnly = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = { limit: 10000 };
            const response = await axios.get('/api/nomenclature', {
                headers: { 'Authorization': `Bearer ${token}` },
                params: params
            });
            const nomenclatureData = response.data?.data || response.data || [];
            setNomenclatures(Array.isArray(nomenclatureData) ? nomenclatureData : []);
            const priceTypesFromNomenclature = response.data?.priceTypes || [];
            if (priceTypesFromNomenclature.length > 0 && priceTypesList.length === 0) {
                setPriceTypesList(priceTypesFromNomenclature);
            }
        } catch (error) {
            console.error('Ошибка при загрузке номенклатуры:', error);
            setNomenclatures([]);
            throw error;
        }
    };

    const fetchExternalTaskData = async (taskId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`/api/tasks/ext/${taskId}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            setExternalTaskData(response.data || []);
        } catch (error) {
            console.error('Ошибка при загрузке данных из внешнего сервиса:', error);
            setExternalTaskData(null);
        }
    };

    const openInvoiceModal = async (taskId, newTaskId, taskStatus) => {
        if (modalIsOpen && currentTaskId === newTaskId && !invoiceLoading) {
            return;
        }

        if (currentTaskId !== newTaskId || !modalIsOpen) {
            setInvoices({ income: [], expense: [] });
            setNewInvoice({
                type: 'income', counterparty: null, article: null, rate_name: '', selectedRateOption: null,
                unit: '', quantity: 1, percent: 100, price: 0, note: '', penalty: 0, include: true
            });
            setEditingInvoiceId(null);
            setIsAddingNewInvoice(false);
            setExternalTaskData(null);
            invoiceForm.resetFields(); // Reset form when opening/changing task
        }

        setCurrentTaskId(newTaskId);
        setTaskId(taskId);
        setCurrentTaskStatus(taskStatus || 'new');
        setModalIsOpen(true);
        setInvoiceLoading(true);

        try {
            await Promise.all([
                fetchPrimaryDictionaries(),
                fetchNomenclaturesOnly(),
                fetchExternalTaskData(newTaskId)
            ]);

            const token = localStorage.getItem('token');
            const invoicesResponse = await axios.get(`/api/tasks/${taskId}/invoices`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setInvoices({
                income: (invoicesResponse.data?.income || []).map(inv => ({...inv})),
                expense: (invoicesResponse.data?.expense || []).map(inv => ({...inv}))
            });
        } catch (error) {
            console.error('Общая ошибка при загрузке данных для модального окна:', error);
            message.error('Не удалось загрузить все необходимые данные. Проверьте консоль.');
            setInvoices({ income: [], expense: [] });
        } finally {
            setInvoiceLoading(false);
        }
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setCurrentTaskId(null);
        setTaskId(null);
        setCurrentTaskStatus('new');
        setEditingInvoiceId(null);
        setIsAddingNewInvoice(false);
        setNewInvoice({
            type: 'income', counterparty: null, article: null, rate_name: '', selectedRateOption: null,
            unit: '', quantity: 1, percent: 100, price: 0, note: '', penalty: 0, include: true
        });
        setExternalTaskData(null);
        invoiceForm.resetFields();
    };

    const handleIncludeChange = (type, id) => {
        setInvoices(prev => ({
            ...prev,
            [type]: prev[type].map(item => item.id === id ? { ...item, include: !item.include } : item)
        }));
    };

    const saveInvoiceChanges = async () => {
        if (editingInvoiceId !== null || isAddingNewInvoice) {
            message.warning("Пожалуйста, сначала сохраните или отмените добавление/редактирование записи.");
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const payloadInvoices = {
                income: invoices.income.map(inv => {
                    const processedInv = { ...inv };
                    if (typeof processedInv.counterparty === 'object' && processedInv.counterparty !== null) {
                        processedInv.counterparty = processedInv.counterparty.label;
                    }
                    if (typeof processedInv.article === 'object' && processedInv.article !== null) {
                        processedInv.article = processedInv.article.label;
                    }
                    return processedInv;
                }),
                expense: invoices.expense.map(inv => {
                    const processedInv = { ...inv };
                    if (typeof processedInv.counterparty === 'object' && processedInv.counterparty !== null) {
                        processedInv.counterparty = processedInv.counterparty.label;
                    }
                    if (typeof processedInv.article === 'object' && processedInv.article !== null) {
                        processedInv.article = processedInv.article.label;
                    }
                    return processedInv;
                }),
            };

            await axios.put(`/api/tasks/${taskId}/invoices`, payloadInvoices, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            message.success('Изменения сохранены');
            fetchTasks(); // Обновляем список задач на главной странице
            closeModal();
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
            if (error.response) {
                console.error('Данные ответа сервера при ошибке:', error.response.data);
                message.error(`Не удалось сохранить изменения: ${error.response.data.message || 'Ошибка сервера'}`);
            } else {
                message.error('Не удалось сохранить изменения: ' + error.message);
            }
        }
    };

    const updateTaskStatus = async (newStatus) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`/api/tasks/${currentTaskId}/status`, { status: newStatus }, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            setCurrentTaskStatus(newStatus);
            setTasks(prevTasks =>
                prevTasks.map(task =>
                    task.id === taskId ? { ...task, status_invoices: newStatus } : task
                )
            );
            message.success('Статус задачи обновлен');
        } catch (error) {
            console.error('Ошибка при обновлении статуса задачи:', error);
            message.error('Не удалось обновить статус задачи');
        }
    };

    const handleFindAndApplyScenario = async () => {
        if (!externalTaskData) {
            message.warning('Данные задачи еще не загружены.');
            return;
        }
        setScenarioCheckLoading(true);
        try {
            const token = localStorage.getItem('token');
            const scenariosResponse = await axios.get('/api/scenarios', {
                params: { limit: 1000 },
                headers: { 'Authorization': `Bearer ${token}` }
            });

            console.clear();
            console.log("--- ЗАПУСК ПОИСКА СЦЕНАРИЕВ ---");
            console.log("Данные текущей задачи (externalTaskData):", externalTaskData);

            const allScenarios = scenariosResponse.data?.data || [];
            if (allScenarios.length === 0) {
                message.warning('Сценарии не найдены в системе.');
                setScenarioCheckLoading(false);
                return;
            }

            console.log(`Получено ${allScenarios.length} сценариев с сервера:`, allScenarios);
            const customFields = externalTaskData.custom_fields ? JSON.parse(externalTaskData.custom_fields) : {};
            console.log("Распарсенные кастомные поля задачи:", customFields);
            console.log("--- НАЧАЛО ПРОВЕРКИ КАЖДОГО СЦЕНАРИЯ ---");

            const matched = [];

            for (const scenario of allScenarios) {
                console.log(`\n%cПроверяем сценарий: "${scenario.scenario_name}" (ID: ${scenario.id})`, 'color: blue; font-weight: bold;');

                if (!scenario.fields || scenario.fields.length === 0) {
                    console.log(`--> Сценарий пропущен, так как у него нет полей для проверки.`);
                    continue;
                }

                let isScenarioMatch = true;
                for (const field of scenario.fields) {
                    console.log(`  * Проверяем поле сценария: "${field.system_name}"`);
                    console.log(`    - Условия в сценарии (possible_values):`, field.possible_values);
                    let isFieldMatch = false;

                    if (field.system_name === 'news_date') {
                        console.log(`    - Тип поля: ДАТА`);
                        const taskDate = externalTaskData.news_date;
                        if (!taskDate) {
                            isFieldMatch = false;
                            console.log(`    - Дата в задаче отсутствует. Результат поля: НЕ СОВПАЛО`);
                        } else if (!field.possible_values || field.possible_values.length === 0) {
                            isFieldMatch = true;
                            console.log(`    - В сценарии нет условий для даты. Результат поля: СОВПАЛО (по умолчанию)`);
                        } else {
                            const conditionChecks = field.possible_values.map(conditionString =>
                                checkDateTimeCondition(taskDate, conditionString)
                            );
                            const results = await Promise.all(conditionChecks);
                            isFieldMatch = results.some(result => result === true);

                            console.log(`    - Асинхронно проверили условия для даты "${taskDate}". Результаты вызовов API:`, results);
                            console.log(`    - Результат поля: ${isFieldMatch ? 'СОВПАЛО' : 'НЕ СОВПАЛО'}`);
                        }
                    } else {
                        console.log(`    - Тип поля: ОБЫЧНОЕ`);
                        let taskValue = getDynamicValue(`<${field.system_name}>`, externalTaskData, customFields);
                        console.log(`    - Значение из задачи для поля "<${field.system_name}>":`, taskValue);

                        if (taskValue === undefined || taskValue === null) {
                            isFieldMatch = false;
                            console.log(`    - Значение в задаче отсутствует. Результат поля: НЕ СОВПАЛО`);
                        } else if (field.possible_values && field.possible_values.length > 0) {
                            const cleanTaskVal = String(taskValue).trim().toLowerCase();
                            isFieldMatch = field.possible_values.some(val =>
                                String(val).trim().toLowerCase() === cleanTaskVal
                            );
                            console.log(`    - Сравниваем "${cleanTaskVal}" с условиями [${field.possible_values.join(', ')}]. Результат поля: ${isFieldMatch ? 'СОВПАЛО' : 'НЕ СОВПАЛО'}`);
                        } else {
                            isFieldMatch = true;
                            console.log(`    - В сценарии нет условий для этого поля. Результат поля: СОВПАЛО (по умолчанию)`);
                        }
                    }

                    if (!isFieldMatch) {
                        isScenarioMatch = false;
                        console.log(`%c--> ИТОГ СЦЕНАРИЯ "${scenario.scenario_name}": НЕ ПОДХОДИТ (из-за поля "${field.system_name}")`, 'color: red;');
                        break;
                    }
                }

                if (isScenarioMatch) {
                    matched.push(scenario);
                    console.log(`%c--> ИТОГ СЦЕНАРИЯ "${scenario.scenario_name}": ПОДХОДИТ!`, 'color: green; font-weight: bold;');
                }
            }

            console.log("\n--- РЕЗУЛЬТАТЫ ПОИСКА ---");
            console.log(`Найдено ${matched.length} подходящих сценариев:`, matched.map(s => s.scenario_name));
            console.log("--- КОНЕЦ ПОИСКА СЦЕНАРИЕВ ---");

            if (matched.length === 0) {
                message.info('Нет подходящих сценариев для данной задачи.');
            } else if (matched.length === 1) {
                applySelectedScenario(matched[0]);
            } else {
                setMatchingScenarios(matched);
                setScenarioModalIsOpen(true);
            }
        } catch (error) {
            console.error("Ошибка при поиске сценариев:", error);
            message.error('Произошла ошибка при поиске подходящих сценариев.');
        } finally {
            setScenarioCheckLoading(false);
        }
    };

    const handleAddNewInlineInitiate = (type) => {
        setIsAddingNewInvoice(true);
        setEditingInvoiceId(null);
        setNewInvoice({
            type: type, counterparty: null, article: null, rate_name: '', selectedRateOption: null,
            unit: '', quantity: 1, percent: 100, price: 0, note: '', penalty: 0, include: true
        });
        invoiceForm.resetFields(); // Reset form for new entry
        // Set initial values for the form to reflect newInvoice defaults
        invoiceForm.setFieldsValue({
            type: type,
            counterparty: null,
            article: null,
            selectedRateOption: null,
            unit: '',
            quantity: 1,
            percent: 100,
            price: 0,
            note: '',
            penalty: 0,
            include: true
        });
    };

    const handleCancelNewInlineEntry = () => {
        setIsAddingNewInvoice(false);
        setNewInvoice({
            type: 'income', counterparty: null, article: null, rate_name: '', selectedRateOption: null,
            unit: '', quantity: 1, percent: 100, price: 0, note: '', penalty: 0, include: true
        });
        invoiceForm.resetFields();
    };

    const handleSaveNewInlineEntry = (type) => {
        invoiceForm.validateFields().then(values => {
            if (!values.counterparty || !values.article || !values.selectedRateOption) {
                message.error("Пожалуйста, заполните Контрагента, Статью и выберите Наименование расценки.");
                return;
            }

            const entryToSave = {
                id: `new-${Date.now()}-${type}`,
                type: type,
                counterparty: values.counterparty.label,
                article: values.article.label,
                rate_name: values.selectedRateOption.name,
                unit: values.selectedRateOption.unit,
                price: values.price,
                quantity: values.quantity,
                percent: values.percent,
                note: values.note,
                penalty: values.penalty,
                include: values.include,
                amount: (values.price || 0) * (values.quantity || 0) * ((values.percent || 0) / 100)
            };
            setInvoices(prev => ({ ...prev, [type]: [entryToSave, ...(prev[type] || [])] }));
            setIsAddingNewInvoice(false);
            setNewInvoice({
                type: type, counterparty: null, article: null, rate_name: '', selectedRateOption: null,
                unit: '', quantity: 1, percent: 100, price: 0, note: '', penalty: 0, include: true
            });
            invoiceForm.resetFields();
        }).catch(info => {
            console.log('Validate Failed:', info);
            message.error("Пожалуйста, заполните все обязательные поля.");
        });
    };

    const startEditingInvoice = (invoice, type) => {
        setEditingInvoiceId(invoice.id);
        setIsAddingNewInvoice(false);

        const preSelectedCounterparty = counterpartyOptions.find(opt => opt.label === invoice.counterparty) || null;
        const preSelectedArticle = (preSelectedCounterparty) ? articleOptionsForEditing(preSelectedCounterparty).find(opt => opt.label === invoice.article) : null;

        let preSelectedRateOption = null;
        if (preSelectedCounterparty && preSelectedArticle) {
            const nomenclatureOpts = nomenclatureOptionsForEditing(preSelectedCounterparty, preSelectedArticle);
            preSelectedRateOption = nomenclatureOpts.find(opt =>
                opt.name === invoice.rate_name &&
                opt.unit === invoice.unit &&
                opt.priceValue === invoice.price
            ) || null;
        }

        invoiceForm.setFieldsValue({
            id: invoice.id,
            counterparty: preSelectedCounterparty,
            article: preSelectedArticle,
            selectedRateOption: preSelectedRateOption,
            rate_name: preSelectedRateOption ? preSelectedRateOption.name : invoice.rate_name,
            unit: preSelectedRateOption ? preSelectedRateOption.unit : invoice.unit,
            price: preSelectedRateOption ? preSelectedRateOption.priceValue : invoice.price,
            quantity: invoice.quantity,
            percent: invoice.percent,
            note: invoice.note,
            penalty: invoice.penalty,
            include: invoice.include,
            type: type
        });
    };

    const saveEditedInvoice = () => {
        invoiceForm.validateFields().then(values => {
            if (!values.counterparty || !values.article || !values.selectedRateOption) {
                message.error("Контрагент, Статья и Наименование расценки должны быть выбраны.");
                return;
            }

            const invoiceToSave = {
                ...values,
                id: editingInvoiceId,
                counterparty: values.counterparty.label,
                article: values.article.label,
                rate_name: values.selectedRateOption.name,
                unit: values.selectedRateOption.unit,
                price: values.price,
                amount: (values.price || 0) * (values.quantity || 0) * ((values.percent || 0) / 100)
            };
            delete invoiceToSave.selectedRateOption;

            setInvoices(prev => ({
                ...prev,
                [invoiceToSave.type]: prev[invoiceToSave.type].map(item =>
                    item.id === invoiceToSave.id ? invoiceToSave : item
                )
            }));
            setEditingInvoiceId(null);
            invoiceForm.resetFields();
        }).catch(info => {
            console.log('Validate Failed:', info);
            message.error("Пожалуйста, заполните все обязательные поля.");
        });
    };

    const deleteInvoice = (type, id) => {
        AntModal.confirm({
            title: 'Удалить запись?',
            content: 'Вы уверены, что хотите удалить эту запись?',
            okText: 'Да',
            cancelText: 'Нет',
            onOk: () => {
                setInvoices(prev => ({
                    ...prev,
                    [type]: prev[type].filter(item => item.id !== id)
                }));
                if (editingInvoiceId === id) {
                    setEditingInvoiceId(null);
                    invoiceForm.resetFields();
                }
            },
        });
    };

    const counterpartyOptions = useMemo(() =>
            counterparties.map(c => ({ value: c.id, label: c.name }))
        , [counterparties]);

    const articleOptionsForEditing = useCallback((currentCounterpartyOption) => {
        if (!currentCounterpartyOption) return [];
        const currentCounterparty = counterparties.find(c => c.id === currentCounterpartyOption.value);
        if (!currentCounterparty || !currentCounterparty.allowed_articles) return [];
        const allowedArticleIds = currentCounterparty.allowed_articles;
        return financialArticles
            .filter(fa => allowedArticleIds.includes(fa.id))
            .map(fa => ({ value: fa.id, label: fa.name }));
    }, [counterparties, financialArticles]);

    const nomenclatureOptionsForEditing = useCallback((currentCounterpartyOption, currentArticleOption) => {
        if (!currentCounterpartyOption || !currentArticleOption) return [];

        const counterpartyId = currentCounterpartyOption.value;
        const articleId = currentArticleOption.value;
        if (!counterpartyId || !articleId) return [];

        const counterpartyData = counterparties.find(c => c.id === counterpartyId);
        if (!counterpartyData || !counterpartyData.price_lists) return [];

        const result = [];
        nomenclatures
            .filter(n => n.article_id === articleId)
            .forEach(n => {
                if (n.prices && typeof n.prices === 'object') {
                    Object.entries(n.prices).forEach(([priceTypeIdStr, priceValue]) => {
                        const priceTypeId = parseInt(priceTypeIdStr);
                        if (counterpartyData.price_lists.includes(priceTypeId) && typeof priceValue === 'number') {
                            const priceTypeInfo = priceTypesList.find(pt => pt.id === priceTypeId);
                            result.push({
                                value: `${n.id}-${priceTypeId}`,
                                label: `${n.name} (${priceTypeInfo ? priceTypeInfo.name : `Тип ${priceTypeId}`}: ${priceValue.toLocaleString('ru-RU')})`,
                                name: n.name,
                                unit: n.unit,
                                priceValue: priceValue,
                            });
                        }
                    });
                }
            });
        return result;
    }, [counterparties, nomenclatures, priceTypesList]);


    const getArticleNameFromId = (articleIdOrName) => {
        if (typeof articleIdOrName === 'object' && articleIdOrName !== null && articleIdOrName.label) {
            return articleIdOrName.label;
        }
        if (typeof articleIdOrName === 'string' && !/^\d+$/.test(articleIdOrName)) {
            return articleIdOrName;
        }
        const numericArticleId = parseInt(String(articleIdOrName).match(/\d+/)?.[0] || String(articleIdOrName), 10);
        if (isNaN(numericArticleId)) return articleIdOrName || '-';
        const article = financialArticles.find(a => a.id === numericArticleId);
        return article ? article.name : `Статья ${numericArticleId}`;
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const exportAllInvoices = async () => {
        const token = localStorage.getItem('token');
        try {
            message.loading({ content: 'Начинается экспорт всех счетов-фактур в Excel...', key: 'exporting_all' });
            const response = await axios.get('/api/invoices/export-all-json', {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const invoicesData = response.data;
            let allInvoicesList = [];
            if (Array.isArray(invoicesData)) {
                allInvoicesList = invoicesData;
            } else if (invoicesData && (invoicesData.income || invoicesData.expense)) {
                allInvoicesList = (invoicesData.income || []).concat(invoicesData.expense || []);
            }

            if (allInvoicesList.length === 0) {
                message.warning({ content: 'Нет данных для экспорта.', key: 'exporting_all', duration: 2 });
                return;
            }

            const getStatusLabel = (statusValue) => {
                const option = TASK_STATUS_OPTIONS.find(opt => opt.value === statusValue);
                return option ? option.label : statusValue || 'Не указан';
            };

            const worksheetData = allInvoicesList.map(inv => ({
                'ID Задачи': inv.task_id,
                'Внешний ID Задачи': inv.task?.external_id || 'Не найден',
                'Статус д/р': getStatusLabel(inv.task?.status_invoices),
                'Тип': inv.type === 'income' ? 'Доход' : 'Расход',
                'ID строки': inv.id,
                'Контрагент': inv.counterparty,
                'Статья': inv.article,
                'Наименование расценки': inv.rate_name,
                'Ед. изм.': inv.unit,
                'Кол-во': parseFloat(inv.quantity) || 0,
                'Процент (%)': parseFloat(inv.percent) || 0,
                'Цена': parseFloat(inv.price) || 0,
                'Стоимость': parseFloat(inv.amount) || 0,
                'Примечание': inv.note,
                'Штраф': parseFloat(inv.penalty) || 0,
                'Учитывать': inv.include ? 'Да' : 'Нет',
                'Ссылка': inv.link,
                'Дата создания': inv.created_at ? new Date(inv.created_at).toLocaleString('ru-RU') : '',
            }));
            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Счета-фактуры');
            XLSX.writeFile(workbook, 'Все_счета_фактуры.xlsx');
            message.success({ content: 'Экспорт в Excel успешно завершен.', key: 'exporting_all', duration: 2 });
        } catch (error) {
            message.error({ content: 'Ошибка при экспорте счетов-фактур в Excel.', key: 'exporting_all', duration: 3 });
            console.error('Export all invoices to Excel error:', error.response || error);
        }
    };

    // --- Task List Columns ---
    const taskColumns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: true },
        { title: 'Внешний ID', dataIndex: 'external_id', key: 'external_id', width: 120 },
        { title: 'Тип', dataIndex: 'type', key: 'type', width: 110 },
        { title: 'Название', dataIndex: 'title', key: 'title', ellipsis: true },
        {
            title: 'Организация создатель',
            key: 'Zakazchik',
            width: 150,
            render: (_, record) => {
                if (record.raw_data && typeof record.raw_data.custom_fields === 'string') {
                    try {
                        const customFields = JSON.parse(record.raw_data.custom_fields);
                        // Проверяем, существует ли Zakazchik и его value
                        return customFields.Zakazchik?.value || '-';
                    } catch (e) {
                        console.error("Ошибка парсинга custom_fields:", e);
                        return 'Ошибка данных';
                    }
                }
                return '-'; // Если custom_fields нет или это не строка
            }
        },
        { title: 'Этап', dataIndex: ['raw_data', 'status_name'], key: 'status_name', width: 120 },
        {
            title: 'Статус д/р',
            dataIndex: 'status_invoices',
            key: 'status_invoices',
            width: 120,
            render: (status) => <Tag color={status === 'approved' ? 'green' : 'blue'}>{TASK_STATUS_OPTIONS.find(s => s.value === status)?.label || status || 'Не указан'}</Tag>
        },
        { title: 'Ответственный', dataIndex: 'assigned_user_fio', key: 'assigned_user_fio', width: 150 },
        {
            title: 'Дата создания',
            dataIndex: 'news_date', // Assuming news_date is the creation date from externalTaskData
            key: 'news_date',
            width: 150,
            render: (date) => date ? new Date(date).toLocaleString('ru-RU') : '-'
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <AntButton
                    type="primary"
                    icon={<FileExcelOutlined />}
                    onClick={() => openInvoiceModal(record.id, record.external_id, record.status_invoices)}
                >
                    ВДР
                </AntButton>
            ),
        },
    ];

    // --- Invoice Table Columns (Income & Expense) ---
    const invoiceTableColumns = (type) => [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
            render: (text, record) => (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id) ? 'Нов.' : text
        },
        {
            title: 'Контрагент',
            dataIndex: 'counterparty',
            key: 'counterparty',
            width: 180,
            render: (text, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                const currentData = invoiceForm.getFieldsValue(); // Get current form values

                if (isEditingThisRow) {
                    return (
                        <Form.Item name="counterparty" rules={[{ required: true, message: 'Выберите контрагента' }]} style={{margin:0}}>
                            <AntSelect
                                options={counterpartyOptions}
                                value={currentData.counterparty}
                                onChange={(selectedOption) => {
                                    if (isAddingNewInvoice) {
                                        setNewInvoice(prev => ({ ...prev, counterparty: selectedOption, article: null, selectedRateOption: null, price: 0, unit: '' }));
                                    }
                                    invoiceForm.setFieldsValue({
                                        counterparty: selectedOption,
                                        article: null,
                                        selectedRateOption: null,
                                        price: 0,
                                        unit: '',
                                    });
                                }}
                                placeholder="Контрагент..."
                                showSearch
                                optionFilterProp="children"
                                allowClear
                            />
                        </Form.Item>
                    );
                }
                return typeof text === 'object' ? text.label : text || '-';
            }
        },
        {
            title: 'Статья',
            dataIndex: 'article',
            key: 'article',
            width: 120,
            render: (text, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                const currentData = invoiceForm.getFieldsValue();
                const articlesOpts = articleOptionsForEditing(currentData.counterparty);

                if (isEditingThisRow) {
                    return (
                        <Form.Item name="article" rules={[{ required: true, message: 'Выберите статью' }]} style={{margin:0}}>
                            <AntSelect
                                options={articlesOpts}
                                value={currentData.article}
                                onChange={(selectedOption) => {
                                    if (isAddingNewInvoice) {
                                        setNewInvoice(prev => ({ ...prev, article: selectedOption, selectedRateOption: null, price: 0, unit: '' }));
                                    }
                                    invoiceForm.setFieldsValue({
                                        article: selectedOption,
                                        selectedRateOption: null,
                                        price: 0,
                                        unit: '',
                                    });
                                }}
                                placeholder="Статья..."
                                disabled={!currentData.counterparty}
                                showSearch
                                optionFilterProp="children"
                                allowClear
                            />
                        </Form.Item>
                    );
                }
                return getArticleNameFromId(text) || '-';
            }
        },
        {
            title: 'Наименование',
            dataIndex: 'rate_name',
            key: 'rate_name',
            width: 300,
            render: (text, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                const currentData = invoiceForm.getFieldsValue();
                const nomenclatureOpts = nomenclatureOptionsForEditing(currentData.counterparty, currentData.article);

                if (isEditingThisRow) {
                    return (
                        <Form.Item name="selectedRateOption" rules={[{ required: true, message: 'Выберите расценку' }]} style={{margin:0}}>
                            <AntSelect
                                options={nomenclatureOpts}
                                value={currentData.selectedRateOption}
                                onChange={(selectedOption) => {
                                    if (isAddingNewInvoice) {
                                        setNewInvoice(prev => ({ ...prev, selectedRateOption: selectedOption, price: selectedOption?.priceValue || 0, unit: selectedOption?.unit || '' }));
                                    }
                                    invoiceForm.setFieldsValue({
                                        selectedRateOption: selectedOption,
                                        price: selectedOption?.priceValue || 0,
                                        unit: selectedOption?.unit || '',
                                    });
                                }}
                                placeholder="Расценка..."
                                disabled={!currentData.article}
                                showSearch
                                optionFilterProp="children"
                                allowClear
                            />
                        </Form.Item>
                    );
                }
                return text || '-';
            }
        },
        {
            title: 'Ед.',
            dataIndex: 'unit',
            key: 'unit',
            width: 60,
            render: (text, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                const currentData = invoiceForm.getFieldsValue();
                return isEditingThisRow ? (
                    <Form.Item name="unit" initialValue={currentData.unit} style={{margin:0}}>
                        <Input readOnly style={{backgroundColor: '#f0f0f0'}} />
                    </Form.Item>
                ) : (
                    text || '-'
                );
            }
        },
        {
            title: 'Кол-во',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'right',
            width: 80,
            render: (text, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                const currentData = invoiceForm.getFieldsValue();
                return isEditingThisRow ? (
                    <Form.Item name="quantity" initialValue={currentData.quantity} rules={[{ required: true, message: 'Введите количество' }]} style={{margin:0}}>
                        <InputNumber min={0} step={0.01} onChange={(val) => {
                            if (isAddingNewInvoice) setNewInvoice(prev => ({ ...prev, quantity: val }));
                            invoiceForm.setFieldsValue({ quantity: val }); // This will trigger recalculation in amount
                        }} style={{width: '100%'}} />
                    </Form.Item>
                ) : (
                    text != null ? text : '-'
                );
            }
        },
        {
            title: '%',
            dataIndex: 'percent',
            key: 'percent',
            align: 'right',
            width: 70,
            render: (text, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                const currentData = invoiceForm.getFieldsValue();
                return isEditingThisRow ? (
                    <Form.Item name="percent" initialValue={currentData.percent} rules={[{ required: true, message: 'Введите процент' }]} style={{margin:0}}>
                        <InputNumber min={0} onChange={(val) => {
                            if (isAddingNewInvoice) setNewInvoice(prev => ({ ...prev, percent: val }));
                            invoiceForm.setFieldsValue({ percent: val }); // This will trigger recalculation in amount
                        }} style={{width: '100%'}} />
                    </Form.Item>
                ) : (
                    text != null ? `${text}%` : '-'
                );
            }
        },
        {
            title: 'Цена',
            dataIndex: 'price',
            key: 'price',
            align: 'right',
            width: 90,
            render: (text, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                const currentData = invoiceForm.getFieldsValue();
                const isPriceEditable = !currentData.selectedRateOption || currentData.selectedRateOption.priceValue === 0;

                return isEditingThisRow ? (
                    <Form.Item name="price" initialValue={currentData.price} style={{margin:0}}>
                        <InputNumber
                            min={0}
                            step={0.01}
                            readOnly={!isPriceEditable}
                            style={{width: '100%', backgroundColor: !isPriceEditable ? '#f0f0f0' : 'white'}}
                            onChange={(val) => {
                                if (isAddingNewInvoice) setNewInvoice(prev => ({ ...prev, price: val }));
                                invoiceForm.setFieldsValue({ price: val }); // This will trigger recalculation in amount
                            }}
                        />
                    </Form.Item>
                ) : (
                    text != null ? parseFloat(text).toLocaleString('ru-RU') : '-'
                );
            }
        },
        {
            title: 'Стоимость',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            width: 100,
            render: (text, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                const currentData = invoiceForm.getFieldsValue();
                const calculatedAmount = (parseFloat(currentData.price) || 0) * (parseFloat(currentData.quantity) || 0) * ((parseFloat(currentData.percent) || 0) / 100);

                return isEditingThisRow ? (
                    <Form.Item name="amount" initialValue={calculatedAmount} style={{margin:0}}>
                        <Input readOnly style={{backgroundColor: '#f0f0f0', fontWeight: 'bold'}} value={calculatedAmount.toLocaleString('ru-RU')} />
                    </Form.Item>
                ) : (
                    text != null ? parseFloat(text).toLocaleString('ru-RU') : '-'
                );
            }
        },
        {
            title: 'Примечание',
            dataIndex: 'note',
            key: 'note',
            width: 150,
            render: (text, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                const currentData = invoiceForm.getFieldsValue();
                return isEditingThisRow ? (
                    <Form.Item name="note" initialValue={currentData.note} style={{margin:0}}>
                        <Input onChange={(e) => {
                            if (isAddingNewInvoice) setNewInvoice(prev => ({ ...prev, note: e.target.value }));
                            invoiceForm.setFieldsValue({ note: e.target.value });
                        }} />
                    </Form.Item>
                ) : (
                    text || '-'
                );
            }
        },
        {
            title: 'Штраф',
            dataIndex: 'penalty',
            key: 'penalty',
            align: 'right',
            width: 80,
            render: (text, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                const currentData = invoiceForm.getFieldsValue();
                return isEditingThisRow ? (
                    <Form.Item name="penalty" initialValue={currentData.penalty} style={{margin:0}}>
                        <InputNumber min={0} onChange={(val) => {
                            if (isAddingNewInvoice) setNewInvoice(prev => ({ ...prev, penalty: val }));
                            invoiceForm.setFieldsValue({ penalty: val }); // This will trigger recalculation in amount
                        }} style={{width: '100%'}} />
                    </Form.Item>
                ) : (
                    text != null ? parseFloat(text).toLocaleString('ru-RU') : '-'
                );
            }
        },
        {
            title: 'Учит.',
            dataIndex: 'include',
            key: 'include',
            align: 'center',
            width: 60,
            render: (text, record) => {
                const isDisabled = editingInvoiceId !== null || isAddingNewInvoice;
                return (
                    <Switch
                        checked={!!text}
                        onChange={() => handleIncludeChange(record.type, record.id)}
                        disabled={isDisabled}
                        size="small"
                    />
                );
            }
        },
        {
            title: 'Связь',
            dataIndex: 'link',
            key: 'link',
            width: 80,
            render: (text) => text ? <a href={text} target="_blank" rel="noopener noreferrer">Ссылка</a> : '-'
        },
        {
            title: 'Действия',
            key: 'actions',
            align: 'center',
            width: 100,
            fixed: 'right',
            render: (_, record) => {
                const isEditingThisRow = (isAddingNewInvoice && record.id === 'new-temp-id') || (editingInvoiceId === record.id);
                // Disable other action buttons if ANY row is being edited or added
                const isDisabled = (editingInvoiceId !== null && editingInvoiceId !== record.id) || isAddingNewInvoice;

                return (
                    <Space size="small">
                        {isEditingThisRow ? (
                            <>
                                <AntButton
                                    icon={<SaveOutlined />}
                                    onClick={() => {
                                        if (isAddingNewInvoice) handleSaveNewInlineEntry(type);
                                        else saveEditedInvoice();
                                    }}
                                    type="primary"
                                    size="small"
                                />
                                <AntButton
                                    icon={<CloseOutlined />}
                                    onClick={() => {
                                        if (isAddingNewInvoice) handleCancelNewInlineEntry();
                                        else setEditingInvoiceId(null);
                                        invoiceForm.resetFields();
                                    }}
                                    danger
                                    size="small"
                                />
                            </>
                        ) : (
                            <>
                                <AntButton
                                    icon={<EditOutlined />}
                                    onClick={() => startEditingInvoice(record, type)}
                                    disabled={isDisabled}
                                    size="small"
                                />
                                <Popconfirm
                                    title="Удалить запись?"
                                    onConfirm={() => deleteInvoice(type, record.id)}
                                    okText="Да"
                                    cancelText="Нет"
                                    disabled={isDisabled}
                                >
                                    <AntButton
                                        icon={<DeleteOutlined />}
                                        danger
                                        disabled={isDisabled}
                                        size="small"
                                    />
                                </Popconfirm>
                            </>
                        )}
                    </Space>
                );
            }
        },
    ];

    return (
        <Layout>
            <div style={{ padding: '20px', width: '95%', margin: '0 auto' }}> {/* <<< ИЗМЕНЕНИЕ: Ширина 95% и центр */}
                <h1 style={{ marginBottom: '20px' }}>Управление задачами</h1>

                {/* Блок поиска и фильтров */}
                <div style={{marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px'}}>
                    <Form layout="vertical">
                        <Row gutter={16} style={{marginBottom: '15px'}}>
                            <Col span={8}>
                                <Input.Search
                                    placeholder="Поиск по внешнему ID, Номер ТТ, название и тд"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onSearch={() => {
                                        setCurrentPage(1);
                                        fetchTasks();
                                    }}
                                    enterButton={<AntButton icon={<SearchOutlined/>}>Поиск</AntButton>}
                                />
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item label="Этапы:">
                                    <AntSelect
                                        options={filterOptions.taskStatuses} // Используем загруженные опции
                                        value={apiFilters.onlyStatus}
                                        onChange={(opts) => handleApiFilterChange('onlyStatus', opts)}
                                        mode="multiple"
                                        placeholder="Выберите этапы"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item label="Стадия:">
                                    <AntSelect
                                        options={[{value: 1, label: 'В работе'}, {value: 2, label: 'Завершено'}]}
                                        value={apiFilters.stage}
                                        onChange={(opts) => handleApiFilterChange('stage', opts)}
                                        mode="multiple"
                                        placeholder="Выберите стадии"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item label="Виды работ:">
                                    <AntSelect
                                        options={filterOptions.taskTypes} // Используем загруженные опции
                                        value={apiFilters.type_id}
                                        onChange={(opts) => handleApiFilterChange('type_id', opts)}
                                        mode="multiple"
                                        placeholder="Выберите виды работ"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item label="Организация исполнитель:">
                                    <AntSelect
                                        options={filterOptions.organizations} // Используем загруженные опции
                                        value={apiFilters.assignedOrganizationId}
                                        onChange={(opts) => handleApiFilterChange('assignedOrganizationId', opts)}
                                        mode="multiple"
                                        placeholder="Выберите организацию"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item label="Организация создатель:">
                                    <AntSelect
                                        options={filterOptions.organizations} // Используем загруженные опции
                                        value={apiFilters.organizationId}
                                        onChange={(opts) => handleApiFilterChange('organizationId', opts)}
                                        mode="multiple"
                                        placeholder="Выберите организацию"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item label="Заказчик:">
                                    <AntSelect
                                        options={filterOptions.customers} // Используем загруженные опции
                                        mode="multiple"
                                        placeholder="Выберите заказчика"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item label="Ответственный:">
                                    <Input
                                        placeholder="ФИО ответственного"
                                        value={apiFilters.assigned_user_fio}
                                        onChange={(e) => handleApiFilterChange('assigned_user_fio', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item label="Дата создания от:">
                                    <Input
                                        type="datetime-local"
                                        value={apiFilters.geDateLocal}
                                        onChange={(e) => handleApiFilterChange('geDateLocal', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item label="Дата создания до:">
                                    <Input
                                        type="datetime-local"
                                        value={apiFilters.leDateLocal}
                                        onChange={(e) => handleApiFilterChange('leDateLocal', e.target.value)}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={6}>
                                <Form.Item label="Статус ВДР:">
                                    <AntSelect
                                        options={TASK_STATUS_OPTIONS}
                                        value={TASK_STATUS_OPTIONS.find(opt => opt.value === apiFilters.status_invoices) || null}
                                        onChange={(selectedOption) => handleApiFilterChange('status_invoices', selectedOption?.value || '')}
                                        allowClear
                                        placeholder="Все статусы"
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Space>
                            <AntButton type="primary" onClick={applyFilters}>
                                Применить фильтры
                            </AntButton>
                            <AntButton danger onClick={resetFilters}>
                                Сбросить фильтры
                            </AntButton>
                        </Space>
                    </Form>
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                    gap: '10px',
                    flexWrap: 'wrap'
                }}>
                    <Space>
                        <AntButton type="primary" icon={<SyncOutlined />} onClick={syncTasks}>Синхронизировать</AntButton>
                        <AntButton type="primary" icon={<DownloadOutlined />} onClick={exportAllInvoices} style={{ backgroundColor: '#FF9800', borderColor: '#FF9800' }}>Экспорт всех с/ф</AntButton>
                    </Space>
                </div>

                <AntTable
                    columns={taskColumns}
                    dataSource={tasks}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalTasks,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            if (pageSize !== size) setPageSize(size);
                        },
                        onShowSizeChange: (current, size) => {
                            setCurrentPage(1); // Reset to first page when page size changes
                            setPageSize(size);
                        }
                    }}
                    bordered
                    scroll={{ x: 1300 }}
                    style={{ marginBottom: '20px' }}
                />

                <ReactModal // Main invoice modal, keep ReactModal
                    isOpen={modalIsOpen}
                    onRequestClose={closeModal}
                    style={{
                        content: {
                            top: '50%',
                            left: '50%',
                            right: 'auto',
                            bottom: 'auto',
                            marginRight: '-50%',
                            transform: 'translate(-50%, -50%)',
                            width: '98%',
                            maxWidth: '98%',
                            height: '98%',
                            maxHeight: '98%',
                            overflow: 'auto',
                            padding: '20px',
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column'
                        },
                        overlay: {backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000}
                    }}
                    contentLabel="Доходы и расходы"
                >
                    {/* !!! ИСПРАВЛЕНИЕ: Оборачиваем все содержимое модального окна в один корневой div !!! */}
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '15px',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '10px',
                            flexShrink: 0
                        }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                                <h2 style={{margin: 0, fontSize: '1.5rem'}}>
                                    Ведомость доходов и расходов для задачи #
                                    <a
                                        href={`https://smartcomservice.geofsm.ru/tasks/#task/${currentTaskId}/`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            marginLeft: '10px',
                                            fontSize: '1rem',
                                            textDecoration: 'underline',
                                            fontSize: '1.5rem'
                                        }}
                                    >
                                        {currentTaskId}
                                    </a>
                                </h2>
                                <div style={{minWidth: '250px'}}>
                                    <AntSelect
                                        options={TASK_STATUS_OPTIONS}
                                        value={TASK_STATUS_OPTIONS.find(opt => opt.value === currentTaskStatus)}
                                        onChange={(selectedOption) => updateTaskStatus(selectedOption.value)}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div style={{minWidth: '250px'}}>
                                    <AntButton
                                        onClick={handleFindAndApplyScenario}
                                        disabled={invoiceLoading || scenarioCheckLoading}
                                        type="primary"
                                        icon={<PlusOutlined />}
                                    >
                                        {scenarioCheckLoading ? 'Проверка...' : 'Заполнить по шаблону'}
                                    </AntButton>
                                </div>
                            </div>
                            <AntButton onClick={closeModal} icon={<CloseOutlined />} type="text" style={{ fontSize: '1.5rem' }} />
                        </div>

                        {invoiceLoading ?
                            <div style={{textAlign: 'center', height: '200px', lineHeight: '200px', flexGrow: 1}}>Загрузка
                                данных...</div> : (
                                <div style={{flexGrow: 1, overflowY: 'auto'}}>
                                    {/* Блок с данными из внешнего сервиса */}
                                    {externalTaskData && (
                                        <div style={{
                                            marginBottom: '30px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            padding: '0px',
                                            backgroundColor: '#f9f9f9'
                                        }}>

                                            {/* Основная информация */}
                                            <div style={{marginBottom: '15px'}}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        cursor: 'pointer',
                                                        padding: '10px',
                                                        backgroundColor: '#f0f0f0',
                                                        borderRadius: '4px'
                                                    }}
                                                    onClick={() => toggleSection('basic')}
                                                >
                                                    <div style={{
                                                        display: 'flex',
                                                        gap: '15px'
                                                    }}>
                                                        <span><b>{externalTaskData.title}</b></span>
                                                        <span><b>Создано: {externalTaskData.news_date}</b></span>
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        gap: '15px'
                                                    }}>
                                                        <Tag>{externalTaskData.custom_fields ? JSON.parse(externalTaskData.custom_fields).Zakazchik?.value || '—' : '—'}</Tag>
                                                        <Tag>{externalTaskData.assigned_user_fio}</Tag>
                                                        <Tag>{externalTaskData.news_type_name}</Tag>
                                                        <Tag>{externalTaskData.status_name}</Tag>
                                                    </div>
                                                    {expandedSections.basic ? <UpOutlined /> : <DownOutlined />}
                                                </div>
                                                {expandedSections.basic && (() => {
                                                    let customFields = {};
                                                    if (typeof externalTaskData?.custom_fields === 'string') {
                                                        try {
                                                            customFields = JSON.parse(externalTaskData.custom_fields);
                                                        } catch (e) {
                                                            console.error("Ошибка парсинга custom_fields:", e);
                                                            return null;
                                                        }
                                                    }

                                                    const timeTo = parseInt(customFields['Doroga_do_obiekta_(minut)']?.value) || 0;
                                                    const timeBack = parseInt(customFields['Doroga_s_obiekta_(minut)']?.value) || 0;
                                                    const totalTime = timeTo + timeBack;
                                                    const confirmedMap = {
                                                        0: 'Отказано',
                                                        1: 'В работе',
                                                        2: 'Завершено',
                                                    };

                                                    const renderField = (label, value) => (
                                                        <>
                                                            <strong>{label}:</strong> { (value || value === 0 || value === false) ? value : '—' }
                                                        </>
                                                    );

                                                    const renderCustomField = (fieldName, label, suffix = '', valueMap = {}) => {
                                                        const value = customFields[fieldName]?.value;
                                                        const displayValue = valueMap[value] !== undefined
                                                            ? valueMap[value]
                                                            : (value || value === 0 || value === false) ? value : '—';
                                                        return (
                                                            <>
                                                                <strong>{label}:</strong> {displayValue}{suffix}
                                                            </>
                                                        );
                                                    };

                                                    return (
                                                        <div style={{fontSize: '14px', padding: '10px'}}>
                                                            <Row gutter={[16, 8]} style={{marginBottom: '10px', border: '1px solid #eee', borderRadius: '4px', padding: '10px'}}>
                                                                <Col span={8}>
                                                                    <Row gutter={[16, 8]}>
                                                                        <Col span={24}>{renderCustomField('Zakazchik', 'Заказчик')}</Col>
                                                                        <Col span={24}>{renderField('Исполнитель', externalTaskData.assigned_user_fio)}</Col>
                                                                        <Col span={24}>{renderCustomField('Nomer_TT', 'Номер ТТ')}</Col>
                                                                    </Row>
                                                                </Col>
                                                                <Col span={8}>
                                                                    <Row gutter={[16, 8]}>
                                                                        <Col span={24}>{renderField('Вид работ', externalTaskData.news_type_name)}</Col>
                                                                        <Col span={24}>{renderField('Этап', externalTaskData.status_name)}</Col>
                                                                        <Col span={24}>{renderField('Стадия', confirmedMap[externalTaskData.confirmed])}</Col>
                                                                    </Row>
                                                                </Col>
                                                                <Col span={8}>
                                                                    <Row gutter={[16, 8]}>
                                                                        <Col span={24}>{renderField('Текст задания', externalTaskData.text)}</Col>
                                                                    </Row>
                                                                </Col>
                                                            </Row>

                                                            <Row gutter={[16, 8]} style={{marginBottom: '10px', border: '1px solid #eee', borderRadius: '4px', padding: '10px'}}>
                                                                <Col span={8}>
                                                                    <Row gutter={[16, 8]}>
                                                                        <Col span={24}>{renderCustomField('Vremja_pojavlenija_na_sayte', 'Прибыл')}</Col>
                                                                        <Col span={24}>{renderCustomField('Vremja_pokidanija_sayta', 'Покинул')}</Col>
                                                                        <Col span={24}>{renderCustomField('Vremja_na_rabote', 'На объекте')}</Col>
                                                                        <Col span={24}>{renderCustomField('CHase_dizelenija', 'Дизеление')}</Col>
                                                                    </Row>
                                                                </Col>
                                                                <Col span={8}>
                                                                    <Row gutter={[16, 8]}>
                                                                        <Col span={24}>{renderField('В дороге, мин', totalTime > 0 ? totalTime : null)}</Col>
                                                                        <Col span={24}>{renderCustomField('Doroga_do_obiekta_(minut)', 'Туда, мин')}</Col>
                                                                        <Col span={24}>{renderCustomField('Doroga_s_obiekta_(minut)', 'Обратно, мин')}</Col>
                                                                        <Col span={24}>{renderCustomField('Probeg,_km', 'Пробег, км')}</Col>
                                                                        <Col span={24}>{renderCustomField('Rasstojanie_do_BS', 'Расстояние по базе')}</Col>
                                                                    </Row>
                                                                </Col>
                                                                <Col span={8}>
                                                                    <Row gutter={[16, 8]}>
                                                                        <Col span={24}>{renderCustomField('Nomer_BS', 'Объект')}</Col>
                                                                        <Col span={24}>{renderCustomField('Adres_BS', 'Адрес')}</Col>
                                                                        <Col span={24}>{renderCustomField('Tip_sayta', 'Тип')}</Col>
                                                                    </Row>
                                                                </Col>
                                                            </Row>

                                                            <Row gutter={[16, 8]} style={{marginBottom: '10px', border: '1px solid #eee', borderRadius: '4px', padding: '10px'}}>
                                                                <Col span={12}>
                                                                    <Row gutter={[16, 8]}>
                                                                        <Col span={24}>{renderCustomField('Opisanie_vepolnenneh_rabot', 'Отчёт для заказчика')}</Col>
                                                                    </Row>
                                                                </Col>
                                                                <Col span={12}>
                                                                    <Row gutter={[16, 8]}>
                                                                        <Col span={24}>{renderCustomField('comments', 'Внутренний отчёт')}</Col>
                                                                    </Row>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    <Form form={invoiceForm} component={false}>
                                        {/* Income Section */}
                                        <div style={{marginBottom: '20px'}}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: '10px',
                                                cursor: 'pointer',
                                                padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px'
                                            }} onClick={() => toggleSection('income')}>
                                                <h3 style={{margin: 0, fontSize: '1.1rem' }}>Доходы</h3>
                                                {expandedSections.income ? <UpOutlined /> : <DownOutlined />}
                                            </div>
                                            {expandedSections.income && (
                                                <>
                                                    <AntButton
                                                        onClick={() => handleAddNewInlineInitiate('income')}
                                                        disabled={isAddingNewInvoice || editingInvoiceId !== null}
                                                        type="primary"
                                                        icon={<PlusOutlined />}
                                                        style={{ marginBottom: '10px' }}
                                                    >
                                                        Добавить Доход
                                                    </AntButton>

                                                    <AntTable
                                                        columns={invoiceTableColumns('income')}
                                                        dataSource={isAddingNewInvoice && newInvoice.type === 'income' ? [{ ...newInvoice, id: 'new-temp-id', type: 'income' }, ...invoices.income] : invoices.income}
                                                        rowKey="id"
                                                        pagination={false}
                                                        bordered
                                                        scroll={{ x: 1300 }}
                                                        style={{ marginBottom: '10px' }}
                                                    />
                                                    {invoices.income && invoices.income.length > 0 && (
                                                        <div style={{ textAlign: 'right', marginTop: '10px', fontWeight: 'bold' }}>
                                                            Итого доходов: {invoices.income
                                                            .filter(inv => inv.include !== false)
                                                            .reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0) + (parseFloat(inv.penalty) || 0), 0)
                                                            .toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Expense Section */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', cursor: 'pointer', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }} onClick={() => toggleSection('expense')}>
                                                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Расходы</h3>
                                                {expandedSections.expense ? <UpOutlined /> : <DownOutlined />}
                                            </div>
                                            {expandedSections.expense && (
                                                <>
                                                    <AntButton
                                                        onClick={() => handleAddNewInlineInitiate('expense')}
                                                        disabled={isAddingNewInvoice || editingInvoiceId !== null}
                                                        type="primary"
                                                        icon={<PlusOutlined />}
                                                        style={{ marginBottom: '10px', backgroundColor: '#FF9800', borderColor: '#FF9800' }}
                                                    >
                                                        Добавить Расход
                                                    </AntButton>

                                                    <AntTable
                                                        columns={invoiceTableColumns('expense')}
                                                        dataSource={isAddingNewInvoice && newInvoice.type === 'expense' ? [{ ...newInvoice, id: 'new-temp-id', type: 'expense' }, ...invoices.expense] : invoices.expense}
                                                        rowKey="id"
                                                        pagination={false}
                                                        bordered
                                                        scroll={{ x: 1300 }}
                                                        style={{ marginBottom: '10px' }}
                                                    />
                                                    {invoices.expense && invoices.expense.length > 0 && (
                                                        <div style={{ textAlign: 'right', marginTop: '10px', fontWeight: 'bold' }}>
                                                            Итого расходов: {invoices.expense
                                                            .filter(inv => inv.include !== false)
                                                            .reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0) + (parseFloat(inv.penalty) || 0), 0)
                                                            .toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* История и комментарии */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', cursor: 'pointer', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }} onClick={() => toggleSection('history')}>
                                                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Вкладка истории и комментариев</h3>
                                                {expandedSections.history ? <UpOutlined /> : <DownOutlined />}
                                            </div>
                                            {expandedSections.history && (
                                                <Input.TextArea rows={4} placeholder="Введите комментарий..." />
                                            )}
                                        </div>

                                        {/* Общий итог */}
                                        <div style={{
                                            marginTop: '20px',
                                            padding: '10px',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '4px',
                                            textAlign: 'right',
                                            fontWeight: 'bold'
                                        }}>
                                            Общий итог: {(
                                            (invoices.income
                                                .filter(inv => inv.include !== false)
                                                .reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0) + (parseFloat(inv.penalty) || 0), 0))
                                            -
                                            (invoices.expense
                                                .filter(inv => inv.include !== false)
                                                .reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0) + (parseFloat(inv.penalty) || 0), 0))
                                        )
                                            .toLocaleString('ru-RU', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </div>
                                    </Form>
                                </div>
                            )
                        }

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                            borderTop: '1px solid #eee',
                            paddingTop: '15px',
                            flexShrink: 0
                        }}>
                            <AntButton onClick={closeModal}>Закрыть</AntButton>
                            <AntButton
                                onClick={saveInvoiceChanges}
                                disabled={isAddingNewInvoice || editingInvoiceId !== null || invoiceLoading}
                                type="primary"
                                icon={<SaveOutlined />}
                            >
                                Сохранить изменения
                            </AntButton>
                        </div>
                    </div>
                </ReactModal>

                <AntModal
                    title="Найдено несколько подходящих сценариев"
                    open={scenarioModalIsOpen}
                    onCancel={() => setScenarioModalIsOpen(false)}
                    footer={[
                        <AntButton key="back" onClick={() => setScenarioModalIsOpen(false)}>Отмена</AntButton>,
                    ]}
                >
                    <p>Пожалуйста, выберите один для применения:</p>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {matchingScenarios.map(scenario => (
                            <AntButton
                                key={scenario.id}
                                onClick={() => applySelectedScenario(scenario)}
                                style={{ width: '100%' }}
                            >
                                {scenario.scenario_name}
                            </AntButton>
                        ))}
                    </Space>
                </AntModal>
            </div>
        </Layout>
    );
};

export default TasksPage;